import { buildServiceCostMutation, deriveServiceCostDraftPreview, serviceCostDraft, validateServiceCostDraft } from "./serviceCost";
import { addQuotationDecimals, calculateQuotationDiscount, calculateQuotationLine, decimalToCanonical, parseQuotationDecimal, subtractQuotationDecimals } from "./quotationForm";
import { normalizeManagedVoidResolution } from "../components/service/ProtectedResolutionField";

const text = (value) => String(value ?? "");
const localKey = () => `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function serviceMemberDraft(ticket) {
  const member = {
    ...ticket,
    customer: {
      company_store_name: text(ticket?.customer?.company_store_name),
      pic: text(ticket?.customer?.pic),
      contact: text(ticket?.customer?.contact),
      address: text(ticket?.customer?.address),
    },
    problem: text(ticket?.problem),
    resolution: text(ticket?.resolution),
    service_cost_draft: serviceCostDraft(ticket?.service_cost),
  };
  return {
    ...member,
    _quotation_original: {
      service_ticket_id: member.service_ticket_id,
      updated_at: member.updated_at,
      customer: { ...member.customer },
      problem: member.problem,
      resolution: member.resolution,
      dvt_warranty_status: member.dvt_warranty_status,
      service_cost: member.service_cost,
    },
  };
}

export function createQuotationDraft(type) {
  return {
    type,
    quotation: null,
    branch: "jakarta",
    service_tickets: [],
    customer_source_ticket_id: "",
    shipping: "0",
    customer: { company_store_name: "", pic: "", contact: "", address: "" },
    serial_numbers: [],
    problem: "",
    resolution: "",
    parts: [],
    custom_costs: [],
    discount_percentage: "0",
    discount_reason: "",
  };
}

export function serviceTicketQuotationDestination(action, ticketId) {
  if (action?.active_quotation_id) {
    return `/quotations/${encodeURIComponent(action.active_quotation_id)}`;
  }
  if (action?.eligible_for_create) {
    return `/quotations/create/sq?ticket=${encodeURIComponent(ticketId)}`;
  }
  return null;
}

export function serviceTicketShowsQuotationAction(ticket) {
  return ticket?.category === "sold";
}

export function serviceTicketQuotationActionLabel(action) {
  return action?.active_quotation_id ? "View Quotation" : "Create Quotation";
}

export function quotationDetailDraft(detail) {
  const type = detail?.quotation_type;
  const draft = createQuotationDraft(type);
  return {
    ...draft,
    quotation: detail,
    branch: detail.branch || "jakarta",
    service_tickets: (detail.service_tickets || []).map(serviceMemberDraft),
    customer_source_ticket_id: detail.customer_source_ticket_id || (type === "sq" ? detail.service_tickets?.[0]?.service_ticket_id || "" : ""),
    shipping: text(detail.shipping ?? "0"),
    customer: { ...draft.customer, ...(detail.customer || {}) },
    serial_numbers: (detail.serial_numbers || []).map(text),
    problem: text(detail.problem),
    resolution: text(detail.resolution),
    parts: (detail.parts || []).map((row) => ({
      ...row,
      _key: row.id || localKey(),
      _persisted_item_id: row.item_id,
      _persisted_part_number: row.part_number,
      _persisted_part_name: row.part_name,
      quantity: text(row.quantity),
      unit_price: text(row.unit_price),
    })),
    custom_costs: (detail.custom_costs || []).map((row) => ({ ...row, _key: row.id || localKey(), quantity: text(row.quantity), unit_price: text(row.unit_price) })),
    discount_percentage: text(detail.discount_percentage ?? "0"),
    discount_reason: text(detail.discount_reason),
  };
}

export function deriveServiceQuotationDraftTotals(draft) {
  try {
    let gross = "0";
    let final = "0";
    for (const member of draft?.service_tickets || []) {
      const preview = deriveServiceCostDraftPreview(member.service_cost, member.service_cost_draft);
      gross = addQuotationDecimals(gross, preview.summary.gross_service_cost);
      final = addQuotationDecimals(final, preview.summary.final_service_cost);
    }
    const shipping = decimalToCanonical(parseQuotationDecimal(draft?.shipping || "0"));
    const uniqueDigit = decimalToCanonical(parseQuotationDecimal(draft?.quotation?.unique_digit || "0"));
    const tax = calculateQuotationLine(gross, 1);
    const provisionalTotal = addQuotationDecimals(final, shipping);
    return {
      gross,
      dpp: tax.line_dpp,
      ppn: tax.line_ppn,
      discount: subtractQuotationDecimals(gross, final),
      service_cost_total: final,
      shipping,
      unique_digit: uniqueDigit,
      provisional_total: provisionalTotal,
      grand_total: addQuotationDecimals(provisionalTotal, uniqueDigit),
    };
  } catch (_error) {
    return null;
  }
}

export function deriveCqDraftTotals(draft) {
  try {
    let gross = "0";
    let dpp = "0";
    let ppn = "0";
    const lineTotals = {};
    for (const row of [...(draft?.parts || []), ...(draft?.custom_costs || [])]) {
      const quantity = Number(row.quantity);
      if (!/^[1-9]\d*$/.test(text(row.quantity)) || !Number.isSafeInteger(quantity)) return null;
      const line = calculateQuotationLine(text(row.unit_price).trim(), quantity);
      lineTotals[row._key || row.id] = line.inclusive_line_total;
      gross = addQuotationDecimals(gross, line.inclusive_line_total);
      dpp = addQuotationDecimals(dpp, line.line_dpp);
      ppn = addQuotationDecimals(ppn, line.line_ppn);
    }
    const percentage = decimalToCanonical(parseQuotationDecimal(draft?.discount_percentage || "0"));
    const discount = calculateQuotationDiscount(gross, percentage);
    const final = subtractQuotationDecimals(gross, discount);
    const shipping = decimalToCanonical(parseQuotationDecimal(draft?.shipping || "0"));
    const uniqueDigit = decimalToCanonical(parseQuotationDecimal(draft?.quotation?.unique_digit || "0"));
    const provisionalTotal = addQuotationDecimals(final, shipping);
    return {
      line_totals: lineTotals,
      gross,
      dpp,
      ppn,
      discount,
      final,
      shipping,
      unique_digit: uniqueDigit,
      provisional_total: provisionalTotal,
      grand_total: addQuotationDecimals(provisionalTotal, uniqueDigit),
    };
  } catch (_error) {
    return null;
  }
}

export function cqPartSelectionState(row, currentItems = []) {
  const usesPersistedSnapshot = Boolean(
    row?.id && row?._persisted_item_id && row?.item_id === row?._persisted_item_id
  );
  return {
    usesPersistedSnapshot,
    snapshotLabel: usesPersistedSnapshot
      ? `${row._persisted_part_number || "-"} · ${row._persisted_part_name || "-"} (saved snapshot)`
      : "",
    selectableItems: usesPersistedSnapshot
      ? currentItems.filter((item) => item.id !== row.item_id)
      : currentItems,
  };
}

export function cqItemModels(currentItems = []) {
  return [...new Set(currentItems.flatMap((item) => [
    item?.model,
    ...(Array.isArray(item?.compatible_models) ? item.compatible_models : []),
  ]).filter(Boolean))].sort();
}

export function cqItemsForModel(currentItems = [], model = "") {
  if (!model) return [];
  return currentItems.filter((item) => (
    item?.model === model
    || (Array.isArray(item?.compatible_models) && item.compatible_models.includes(model))
  ));
}

const same = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const customerValue = (value) => ({
  company_store_name: text(value?.company_store_name).trim(),
  pic: text(value?.pic).trim(),
  contact: text(value?.contact).trim(),
  address: text(value?.address).trim(),
});

const canonicalAmount = (value) => {
  try { return decimalToCanonical(parseQuotationDecimal(text(value || "0").trim())); } catch { return text(value); }
};
export function normalizeActiveQuotationDraft(draft) {
  return {
    type: text(draft?.type), branch: text(draft?.branch),
    customer_source_ticket_id: text(draft?.customer_source_ticket_id),
    shipping: canonicalAmount(draft?.shipping), customer: customerValue(draft?.customer),
    serial_numbers: (draft?.serial_numbers || []).map((v) => text(v).trim()).filter(Boolean),
    problem: text(draft?.problem).trim(), resolution: text(draft?.resolution).trim(),
    discount_percentage: canonicalAmount(draft?.discount_percentage), discount_reason: text(draft?.discount_reason).trim(),
    service_tickets: (draft?.service_tickets || []).map((member) => ({
      service_ticket_id: text(member.service_ticket_id),
      customer: customerValue(member.customer), problem: text(member.problem).trim(),
      resolution: text(member.resolution).trim(),
      service_cost_draft: member.service_cost_draft || {},
    })),
    parts: (draft?.parts || []).map((row) => ({ item_id: text(row.item_id), quantity: Number(row.quantity || 0), unit_price: canonicalAmount(row.unit_price) })),
    custom_costs: (draft?.custom_costs || []).map((row) => ({ description: text(row.description).trim(), quantity: Number(row.quantity || 0), unit_price: canonicalAmount(row.unit_price) })),
  };
}

export function buildServiceMutation(original, current, includeCustomer) {
  const mutation = {
    service_ticket_id: current.service_ticket_id,
    expected_updated_at: original.updated_at,
  };
  if (includeCustomer && !same(customerValue(original.customer), customerValue(current.customer))) {
    mutation.customer = customerValue(current.customer);
  }
  if (text(original.problem).trim() !== text(current.problem).trim()) mutation.problem = text(current.problem).trim();
  const originalResolution = normalizeManagedVoidResolution(
    original.resolution,
    original.dvt_warranty_status === "void"
  );
  const currentResolution = normalizeManagedVoidResolution(
    current.resolution,
    current.dvt_warranty_status === "void"
  );
  if (originalResolution !== currentResolution) mutation.resolution = currentResolution;
  Object.assign(mutation, buildServiceCostMutation(original.service_cost, current.service_cost_draft, original.updated_at));
  return Object.keys(mutation).length > 2 ? mutation : null;
}

export function buildActiveQuotationPayload(draft, persisted = null) {
  const type = draft.type;
  if (type === "cq") {
    const payload = {
      quotation_type: "cq",
      branch: draft.branch,
      customer: customerValue(draft.customer),
      serial_numbers: draft.serial_numbers.map((value) => value.trim()).filter(Boolean),
      problem: draft.problem.trim(),
      resolution: draft.resolution.trim(),
      parts: draft.parts.map((row) => ({
        ...(row.id ? { id: row.id } : {}),
        item_id: row.item_id,
        quantity: Number(row.quantity),
        unit_price: text(row.unit_price).trim(),
      })),
      custom_costs: draft.custom_costs.map((row) => ({
        ...(row.id ? { id: row.id } : {}),
        description: text(row.description).trim(),
        quantity: Number(row.quantity),
        unit_price: text(row.unit_price).trim(),
      })),
      discount_percentage: text(draft.discount_percentage || "0").trim(),
      discount_reason: text(draft.discount_reason).trim(),
      shipping: text(draft.shipping || "0").trim(),
    };
    if (persisted) {
      delete payload.quotation_type;
      payload.expected_updated_at = persisted.updated_at;
    }
    return payload;
  }

  const originals = new Map((persisted?.service_tickets || draft.service_tickets).map((ticket) => [
    ticket.service_ticket_id,
    persisted ? ticket : ticket._quotation_original || ticket,
  ]));
  const serviceMutations = draft.service_tickets.map((ticket) => {
    const original = originals.get(ticket.service_ticket_id) || ticket;
    return buildServiceMutation(
      original,
      ticket,
      type === "sq" || ticket.service_ticket_id === draft.customer_source_ticket_id
    );
  }).filter(Boolean);
  const payload = {
    ...(persisted ? {} : { quotation_type: type }),
    service_ticket_ids: draft.service_tickets.map((ticket) => ticket.service_ticket_id),
    shipping: text(draft.shipping || "0").trim(),
    service_mutations: serviceMutations,
  };
  if (type === "mq") payload.customer_source_ticket_id = draft.customer_source_ticket_id;
  if (persisted) payload.expected_updated_at = persisted.updated_at;
  return payload;
}

const money = (value) => /^\d+(?:\.\d+)?$/.test(text(value).trim());

export function validateActiveQuotation(draft, { currentItemIds = null } = {}) {
  if (!money(draft.shipping)) return "Shipping must be a non-negative amount.";
  if (draft.type === "sq" && draft.service_tickets.length !== 1) return "SQ requires exactly one Service Ticket.";
  if (draft.type === "mq") {
    if (draft.service_tickets.length < 2) return "MQ requires at least two Service Tickets.";
    if (!draft.customer_source_ticket_id || !draft.service_tickets.some((row) => row.service_ticket_id === draft.customer_source_ticket_id)) {
      return "Select a Customer Source from the current MQ members.";
    }
  }
  if (["sq", "mq"].includes(draft.type)) {
    for (const member of draft.service_tickets) {
      const error = validateServiceCostDraft(member.service_cost_draft);
      if (error) return `${member.service_ticket_number || "Service Ticket"}: ${error}`;
    }
    return "";
  }
  if (!draft.customer.company_store_name.trim() || !draft.customer.contact.trim()) return "Company / Store Name and Contact are required.";
  if (!draft.serial_numbers.some((value) => value.trim())) return "Add at least one Serial Number.";
  if (!draft.problem.trim()) return "Problem is required.";
  if (!draft.resolution.trim()) return "Resolution is required.";
  if (!draft.parts.length && !draft.custom_costs.length) return "CQ requires at least one Part or Custom Cost.";
  for (const row of [...draft.parts, ...draft.custom_costs]) {
    if (!/^[1-9]\d*$/.test(text(row.quantity)) || !money(row.unit_price)) return "Every cost line needs a positive Quantity and non-negative Unit Price.";
  }
  if (draft.parts.some((row) => !row.item_id)) return "Select an Item for every Part line.";
  if (currentItemIds) {
    const validItemIds = currentItemIds instanceof Set ? currentItemIds : new Set(currentItemIds);
    const invalidSelection = draft.parts.some((row) => {
      const unchangedSnapshot = row.id && row._persisted_item_id && row.item_id === row._persisted_item_id;
      return !unchangedSnapshot && !validItemIds.has(row.item_id);
    });
    if (invalidSelection) return "New or replaced Part lines must select a current Item.";
  }
  if (draft.custom_costs.some((row) => !text(row.description).trim())) return "Custom Cost description is required.";
  if (!/^\d+(?:\.\d{1,2})?$/.test(text(draft.discount_percentage)) || Number(draft.discount_percentage) > 100) return "Discount must be 0..100 with at most two decimals.";
  if (Number(draft.discount_percentage) > 0 && !draft.discount_reason.trim()) return "Discount Reason is required above 0%.";
  return "";
}

export { localKey };

export function activeQuotationError(error) {
  const nested = error?.response?.data?.detail?.error;
  if (error?.response?.status === 409 || nested?.code === "SERVICE_QUOTATION_CONFLICT") {
    return "This quotation or a linked Service Ticket changed in another session. Reload the latest data before retrying.";
  }
  return nested?.message || error?.message || "Quotation request failed.";
}
